import { z } from 'zod';

// ============================================================================
// CRM gRPC Response DTOs (Issue #314)
// ============================================================================
// These types represent the raw gRPC response structures from CRM Service.
// They use snake_case field names matching the protobuf definitions.
// Services should map these to camelCase API responses.

/**
 * Commission response from CRM gRPC service (snake_case from proto)
 * @see crm-service.proto CommissionResponse
 * @see Issue #390 - Employee Commission Split
 */
export const CommissionGrpcResponseSchema = z.object({
  id: z.string(),
  business_id: z.string(),
  partner_id: z.string(),
  case_id: z.string().nullable().optional(),
  referral_id: z.string().nullable().optional(),
  amount: z.string(), // Decimal as string from proto
  rate: z.string(), // Decimal as string from proto
  status: z.string(),
  paid_at: z.string().nullable().optional(), // ISO 8601
  payment_method: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  metadata: z.string().nullable().optional(), // JSON string
  created_at: z.string(), // ISO 8601
  updated_at: z.string(), // ISO 8601

  // Employee commission tracking (Issue #390)
  referring_employee_id: z.string().nullable().optional(),
  referring_employee_name: z.string().nullable().optional(),
  referring_employee_email: z.string().nullable().optional(),

  // Commission split fields (Issue #390)
  employee_rate: z.string().nullable().optional(), // Decimal as string
  company_rate: z.string().nullable().optional(), // Decimal as string
  employee_amount: z.string().nullable().optional(), // Decimal as string
  company_amount: z.string().nullable().optional(), // Decimal as string

  // Individual payout tracking (Issue #390)
  employee_paid_at: z.string().nullable().optional(), // ISO 8601
  company_paid_at: z.string().nullable().optional(), // ISO 8601
  employee_payment_method: z.string().nullable().optional(),
  company_payment_method: z.string().nullable().optional(),
  employee_payment_ref: z.string().nullable().optional(),
  company_payment_ref: z.string().nullable().optional(),
});

export type CommissionGrpcResponse = z.infer<typeof CommissionGrpcResponseSchema>;

/**
 * Commission Payout response from CRM gRPC service (snake_case from proto)
 * @see Issue #390 - Employee Commission Split
 */
export const CommissionPayoutGrpcResponseSchema = z.object({
  id: z.string(),
  commission_id: z.string(),
  recipient_type: z.string(), // EMPLOYEE | COMPANY | OVERRIDE | REFERRER
  recipient_id: z.string(),
  recipient_name: z.string(),
  recipient_email: z.string().nullable().optional(),
  recipient_phone: z.string().nullable().optional(),
  rate: z.string(), // Decimal as string
  amount: z.string(), // Decimal as string
  split_percent: z.string().nullable().optional(), // Decimal as string
  status: z.string(), // pending | approved | processing | paid | cancelled | on_hold
  approved_at: z.string().nullable().optional(), // ISO 8601
  approved_by: z.string().nullable().optional(),
  paid_at: z.string().nullable().optional(), // ISO 8601
  payment_method: z.string().nullable().optional(),
  payment_ref: z.string().nullable().optional(),
  payment_batch: z.string().nullable().optional(),
  clawback_amount: z.string().nullable().optional(), // Decimal as string
  clawback_reason: z.string().nullable().optional(),
  clawback_at: z.string().nullable().optional(), // ISO 8601
  clawback_by: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  created_at: z.string(), // ISO 8601
  updated_at: z.string(), // ISO 8601
});

export type CommissionPayoutGrpcResponse = z.infer<typeof CommissionPayoutGrpcResponseSchema>;

/**
 * Partner Commission Split configuration from CRM gRPC service
 * @see Issue #390 - Employee Commission Split
 */
export const PartnerCommissionSplitGrpcResponseSchema = z.object({
  id: z.string(),
  business_id: z.string(),
  partner_id: z.string(),
  split_method: z.string(), // percentage | fixed_employee | fixed_company | rate_based | tiered
  employee_split_percent: z.string().nullable().optional(), // Decimal as string
  company_split_percent: z.string().nullable().optional(), // Decimal as string
  employee_fixed_amount: z.string().nullable().optional(), // Decimal as string
  company_fixed_amount: z.string().nullable().optional(), // Decimal as string
  employee_rate: z.string().nullable().optional(), // Decimal as string
  company_rate: z.string().nullable().optional(), // Decimal as string
  tiers: z.string().nullable().optional(), // JSON string
  minimum_employee_payout: z.string().nullable().optional(), // Decimal as string
  minimum_company_payout: z.string().nullable().optional(), // Decimal as string
  vesting_enabled: z.boolean().optional(),
  vesting_schedule: z.string().nullable().optional(), // JSON string
  effective_date: z.string(), // ISO 8601
  expiration_date: z.string().nullable().optional(), // ISO 8601
  is_active: z.boolean(),
  notes: z.string().nullable().optional(),
  created_by: z.string(),
  created_at: z.string(), // ISO 8601
  updated_at: z.string(), // ISO 8601
});

export type PartnerCommissionSplitGrpcResponse = z.infer<typeof PartnerCommissionSplitGrpcResponseSchema>;

/**
 * Employee Commission Override from CRM gRPC service
 * @see Issue #390 - Employee Commission Split
 */
export const EmployeeCommissionOverrideGrpcResponseSchema = z.object({
  id: z.string(),
  business_id: z.string(),
  partner_id: z.string(),
  employee_id: z.string(),
  employee_name: z.string(),
  override_rate: z.string().nullable().optional(), // Decimal as string
  override_percent: z.string().nullable().optional(), // Decimal as string
  override_type: z.string(), // rate | percent | fixed
  fixed_amount: z.string().nullable().optional(), // Decimal as string
  effective_date: z.string(), // ISO 8601
  expiration_date: z.string().nullable().optional(), // ISO 8601
  is_active: z.boolean(),
  reason: z.string().nullable().optional(),
  created_by: z.string(),
  approved_by: z.string().nullable().optional(),
  approved_at: z.string().nullable().optional(), // ISO 8601
  created_at: z.string(), // ISO 8601
  updated_at: z.string(), // ISO 8601
});

export type EmployeeCommissionOverrideGrpcResponse = z.infer<typeof EmployeeCommissionOverrideGrpcResponseSchema>;

/**
 * Partner response from CRM gRPC service (snake_case from proto)
 * @see crm-service.proto PartnerResponse
 */
export const PartnerGrpcResponseSchema = z.object({
  id: z.string(),
  business_id: z.string(),
  consumer_id: z.string(),
  type: z.string(), // 'referral' | 'white_label' | etc.
  status: z.string(), // 'active' | 'inactive' | 'pending'
  commission_rate: z.string(), // Decimal as string from proto
  metadata: z.string().nullable().optional(), // JSON string
  verified_at: z.string().nullable().optional(), // ISO 8601
  verified_by: z.string().nullable().optional(),
  created_at: z.string(), // ISO 8601
  updated_at: z.string(), // ISO 8601
});

export type PartnerGrpcResponse = z.infer<typeof PartnerGrpcResponseSchema>;

/**
 * Referral response from CRM gRPC service (snake_case from proto)
 * @see crm-service.proto ReferralResponse
 */
export const ReferralGrpcResponseSchema = z.object({
  id: z.string(),
  business_id: z.string(),
  partner_id: z.string(),
  client_name: z.string(),
  client_email: z.string(),
  referral_type: z.string(),
  status: z.string(),
  notes: z.string().nullable().optional(),
  converted_to_case: z.boolean().optional(),
  created_at: z.string(), // ISO 8601
  updated_at: z.string(), // ISO 8601
});

export type ReferralGrpcResponse = z.infer<typeof ReferralGrpcResponseSchema>;

// ============================================================================
// List Response Wrappers
// ============================================================================

export const ListCommissionsGrpcResponseSchema = z.object({
  commissions: z.array(CommissionGrpcResponseSchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
});

export type ListCommissionsGrpcResponse = z.infer<typeof ListCommissionsGrpcResponseSchema>;

export const ListPartnersGrpcResponseSchema = z.object({
  partners: z.array(PartnerGrpcResponseSchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
});

export type ListPartnersGrpcResponse = z.infer<typeof ListPartnersGrpcResponseSchema>;

export const ListReferralsGrpcResponseSchema = z.object({
  referrals: z.array(ReferralGrpcResponseSchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
});

export type ListReferralsGrpcResponse = z.infer<typeof ListReferralsGrpcResponseSchema>;

// ============================================================================
// Partner Trend DTOs (Issue #1739)
// ============================================================================
// Month-over-month conversion-rate trend used by AstorPartnerTrendCard on the
// partner detail page. `conversion_pct_change_mo_m` is null when previous-month
// rate is 0 — pct change is mathematically undefined.

/**
 * GetPartnerTrend gRPC response (snake_case from proto)
 * @see crm-service.proto PartnerTrendResponse
 */
export const PartnerTrendGrpcResponseSchema = z.object({
  current_month_referrals: z.number().int(),
  previous_month_referrals: z.number().int(),
  current_month_conversions: z.number().int(),
  previous_month_conversions: z.number().int(),
  current_conversion_rate: z.number(), // 0-100
  previous_conversion_rate: z.number(), // 0-100
  conversion_pct_change_mo_m: z.number().nullable(),
});

export type PartnerTrendGrpcResponse = z.infer<typeof PartnerTrendGrpcResponseSchema>;

/**
 * camelCase PartnerTrend DTO surfaced by tRPC partnerInvitations.getPartner.
 * The frontend consumes this shape directly on the partner detail page.
 */
export const PartnerTrendDtoSchema = z.object({
  currentMonthReferrals: z.number().int(),
  previousMonthReferrals: z.number().int(),
  currentMonthConversions: z.number().int(),
  previousMonthConversions: z.number().int(),
  currentConversionRate: z.number(), // 0-100
  previousConversionRate: z.number(), // 0-100
  conversionPctChangeMoM: z.number().nullable(),
});

export type PartnerTrendDto = z.infer<typeof PartnerTrendDtoSchema>;

// ============================================================================
// gRPC Error Types (Issue #314)
// ============================================================================
// Standard gRPC error structure for typed error handling

/**
 * Standard gRPC status codes
 * @see https://grpc.github.io/grpc/core/md_doc_statuscodes.html
 */
export const GrpcStatusCode = {
  OK: 0,
  CANCELLED: 1,
  UNKNOWN: 2,
  INVALID_ARGUMENT: 3,
  DEADLINE_EXCEEDED: 4,
  NOT_FOUND: 5,
  ALREADY_EXISTS: 6,
  PERMISSION_DENIED: 7,
  RESOURCE_EXHAUSTED: 8,
  FAILED_PRECONDITION: 9,
  ABORTED: 10,
  OUT_OF_RANGE: 11,
  UNIMPLEMENTED: 12,
  INTERNAL: 13,
  UNAVAILABLE: 14,
  DATA_LOSS: 15,
  UNAUTHENTICATED: 16,
} as const;

export type GrpcStatusCodeType = (typeof GrpcStatusCode)[keyof typeof GrpcStatusCode];

/**
 * gRPC Error interface for typed error handling
 * Use this instead of `any` in catch blocks when handling gRPC errors
 *
 * @example
 * ```typescript
 * } catch (error: unknown) {
 *   const grpcError = error as GrpcError;
 *   if (grpcError.code === GrpcStatusCode.NOT_FOUND) {
 *     throw new NotFoundException('Resource not found');
 *   }
 *   throw error;
 * }
 * ```
 */
export interface GrpcError {
  code?: GrpcStatusCodeType;
  message?: string;
  details?: string;
  metadata?: Record<string, string>;
}
